import './share-on.scss';
import Tooltip from 'bootstrap-italia/src/js/plugins/tooltip';
import { Translations } from '../utils';

/**
 * version     1.2
 * class       ShareOn
 * classdesc   Component that manages the sharing functionality for each property in the portfolio
 */
export class ShareOn {
  constructor() {
    this._tooltipCache = new WeakMap();
    this.init();
  }

  init() {
    /**
     * Toggle dropdown — delegated to document so it fires even for cards
     * that are injected into the DOM after this constructor runs.
     */
    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('.share-on .btn-share');
      if (toggleBtn) {
        e.stopPropagation();
        const component = toggleBtn.closest('.share-on');
        const dropdown = component.querySelector('.btn-share__dropdown');
        if (!dropdown) return;

        const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
        this._closeAll(toggleBtn);
        toggleBtn.setAttribute('aria-expanded', String(!isExpanded));
        dropdown.hidden = isExpanded;

        if (!isExpanded) {
          const firstFocusable = dropdown.querySelector('button, a');
          if (firstFocusable) firstFocusable.focus();
        }
        return;
      }

      /**
       * Chiudi con il bottone X
       */
      const closeBtn = e.target.closest('.share-on .btn-share__close');
      if (closeBtn) {
        e.stopPropagation();
        const component = closeBtn.closest('.share-on');
        const toggleBtnInner = component.querySelector('.btn-share');
        const dropdown = component.querySelector('.btn-share__dropdown');
        if (toggleBtnInner && dropdown) {
          this._close(toggleBtnInner, dropdown);
          toggleBtnInner.focus();
        }
        return;
      }

      /**
       * Condividi via mail
       */
      const mailBtn = e.target.closest('.share-on .share-mail');
      if (mailBtn) {
        const component = mailBtn.closest('.share-on');
        const shareUrl = this._resolveShareUrl(component);
        const subject = encodeURIComponent(document.title);
        const body = encodeURIComponent(shareUrl);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
        return;
      }

      /**
       * Copia link
       */
      const copyBtn = e.target.closest('.share-on .copy-link');
      if (copyBtn) {
        const component = copyBtn.closest('.share-on');
        const shareUrl = this._resolveShareUrl(component);
        this._handleCopy(copyBtn, shareUrl);
        return;
      }

      /**
       * Click fuori dal componente — chiude tutti i dropdown aperti
       */
      if (!e.target.closest('.share-on')) {
        this._closeAll();
      }
    });

    /**
     * ESC per chiudere
     */
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const openBtn = document.querySelector('.share-on .btn-share[aria-expanded="true"]');
      if (openBtn) {
        const dropdown = openBtn.closest('.share-on').querySelector('.btn-share__dropdown');
        if (dropdown) this._close(openBtn, dropdown);
        openBtn.focus();
      }
    });
  }

  /**
   * Ricava l'URL da condividere:
   * cerca il link "Vedi immobile" nella stessa .cdp-card__cta (portfolio),
   * altrimenti usa l'URL della pagina corrente (es. pagina del singolo immobile).
   */
  _resolveShareUrl(component) {
    const cta = component.closest('.cdp-card__cta');
    const cardLink = cta ? cta.querySelector('.immobile-link') : null;
    return cardLink ? cardLink.href : window.location.href;
  }

  /**
   * Copia il link negli appunti e mostra tooltip di conferma.
   * Il Tooltip viene inizializzato la prima volta che il bottone viene cliccato
   * e poi riusato tramite WeakMap, evitando re-init su elementi dinamici.
   */
  async _handleCopy(copyBtn, shareUrl) {
    if (!this._tooltipCache.has(copyBtn)) {
      this._tooltipCache.set(
        copyBtn,
        new Tooltip(copyBtn, {
          trigger: 'manual',
          placement: 'top',
          title: Translations.getLabel('SHARE_ON_LINK_COPIED', {}, 'Link copiato!'),
        }),
      );
    }
    const tooltip = this._tooltipCache.get(copyBtn);

    const showTooltip = () => {
      setTimeout(() => {
        copyBtn.focus();
        tooltip.show();
        setTimeout(() => tooltip.hide(), 2500);
      }, 50);
    };

    const copyFallback = (text) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        return successful;
      } catch (err) {
        document.body.removeChild(textarea);
        return false;
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        showTooltip();
      } catch (err) {
        if (copyFallback(shareUrl)) showTooltip();
      }
    } else if (copyFallback(shareUrl)) {
      showTooltip();
    }
  }

  _close(toggleBtn, dropdown) {
    toggleBtn.setAttribute('aria-expanded', 'false');
    dropdown.setAttribute('hidden', '');
  }

  _closeAll(excludeToggle = null) {
    document.querySelectorAll('.share-on .btn-share[aria-expanded="true"]').forEach((btn) => {
      if (btn !== excludeToggle) {
        const dropdown = btn.closest('.share-on').querySelector('.btn-share__dropdown');
        if (dropdown) this._close(btn, dropdown);
      }
    });
  }
}
